import { isDevelopment } from "../utils/settings";
import { AppState, hexHeight, hexWidth } from "./State";

export function compareObjects(obj1: any, obj2: any): string {
  const diff: string[] = [];
  for (const key in obj1) {
    if (obj1.hasOwnProperty(key)) {
      if (obj2 === null || !(key in obj2)) {
        diff.push(`- ${key}: ${JSON.stringify(obj1[key], null, 2)}`);
      } else if (
        typeof obj1[key] === "object" &&
        typeof obj2[key] === "object"
      ) {
        const nestedDiff = compareObjects(obj1[key], obj2[key]);
        if (nestedDiff !== " No changes") {
          diff.push(`${key}:\n${nestedDiff}`);
        }
      } else if (JSON.stringify(obj1[key]) !== JSON.stringify(obj2[key])) {
        diff.push(
          ` ${key}: ${JSON.stringify(obj1[key], null, 2)} -> ${JSON.stringify(
            obj2[key],
            null,
            2
          )}`
        );
      }
    }
  }
  for (const key in obj2) {
    if (obj2.hasOwnProperty(key)) {
      if (obj1 === null || !(key in obj1)) {
        diff.push(`+ ${key}: ${JSON.stringify(obj2[key], null, 2)}`);
      }
    }
  }
  return diff.join("\n") || " No changes";
}

const HEX_VISUAL_SIZE = 256;

/** World coords (worldX, worldY) so that hex (x, y) is centered on the viewport. Use for map centering from links, search, menus, etc. */
export function getWorldCoordsToCenterHex(
  x: number,
  y: number,
  worldScale: number
): { worldX: number; worldY: number } {
  const w = hexWidth * worldScale;
  const h = hexHeight * worldScale;
  const evenGap = y % 2 ? w / 2 : 0;
  const left = x * w + evenGap;
  const top = y * h - hexHeight / 2;
  const hexSize = HEX_VISUAL_SIZE * worldScale;
  const cx = left + hexSize / 2;
  const cy = top + hexSize / 2;
  return {
    worldX: typeof window !== "undefined" ? window.innerWidth / 2 - cx : 0,
    worldY: typeof window !== "undefined" ? window.innerHeight / 2 - cy : 0,
  };
}

/** Hex center position in content (world) pixels. Must match World layout. */
function hexCenterInWorld(
  x: number,
  y: number,
  scale: number
): { cx: number; cy: number } {
  const w = hexWidth * scale;
  const h = hexHeight * scale;
  const evenGap = y % 2 ? w / 2 : 0;
  const left = x * w + evenGap;
  const top = y * h - hexHeight / 2;
  return { cx: left + w / 2, cy: top + h / 2 };
}

/** Visible hex bounds from World (worldX, worldY, scale, buffer). Used to get center hex for URL. */
function getVisibleHexBounds(
  worldX: number,
  worldY: number,
  worldScale: number,
  hexGridBuffer: number
): { minX: number; minY: number; maxX: number; maxY: number } {
  const winW = typeof window !== "undefined" ? window.innerWidth : 800;
  const winH = typeof window !== "undefined" ? window.innerHeight : 600;
  const W = hexWidth * worldScale;
  const H = hexHeight * worldScale;
  const buf = hexGridBuffer;
  const contentLeft = -worldX;
  const contentRight = winW - worldX;
  const contentTop = -worldY;
  const contentBottom = winH - worldY;
  const baseMinX = Math.floor((contentLeft - W / 2) / W);
  const baseMaxX = Math.ceil((contentRight + W / 2) / W) - 1;
  const baseMinY = Math.floor((contentTop - hexHeight / 2) / H);
  const baseMaxY = Math.floor((contentBottom + hexHeight / 2) / H);
  return {
    minX: baseMinX - buf,
    maxX: baseMaxX + buf,
    minY: baseMinY - buf,
    maxY: baseMaxY + buf,
  };
}

/** Center hex (closest to viewport center) for current map position. Used when closing hex popup to set URL. */
export function getCenterHexFromWorld(
  worldX: number,
  worldY: number,
  worldScale: number,
  hexGridBuffer: number
): { x: number; y: number } | null {
  if (typeof window === "undefined") return null;
  const bounds = getVisibleHexBounds(worldX, worldY, worldScale, hexGridBuffer);
  const refX = window.innerWidth / 2 - worldX;
  const refY = window.innerHeight / 2 - worldY;
  let best: { x: number; y: number } | null = null;
  let bestD = Infinity;
  for (let x = bounds.minX; x <= bounds.maxX; x++) {
    for (let y = bounds.minY; y <= bounds.maxY; y++) {
      const { cx, cy } = hexCenterInWorld(x, y, worldScale);
      const d = (cx - refX) ** 2 + (cy - refY) ** 2;
      if (d < bestD) {
        bestD = d;
        best = { x, y };
      }
    }
  }
  return best;
}

export const calculateNewWorldCoordinates = (
  state: AppState,
  newScale: number
) => {
  const { worldX, worldY, worldScale } = state;
  const centrX = worldX - window.innerWidth / 2;
  const centrY = worldY - window.innerHeight / 2;
  const zeroX = centrX / worldScale;
  const zeroY = centrY / worldScale;
  const newWorldX = zeroX * newScale + window.innerWidth / 2;
  const newWorldY = zeroY * newScale + window.innerHeight / 2;
  return { worldX: newWorldX, worldY: newWorldY };
};

const initCoordinates = isDevelopment
  ? [
      [0, 0],
      [5, 5],
      [5, -5],
      [-5, 5],
      [-5, -5],
    ]
  : [
      [0, 0],
      [5, 110],
      [-12, -100],
      [-70, -50],
      [-80, -50],
      [-115, -15],
      [-135, -5],
      [-155, 20],
      [-50, 70],
      [55, 100],
      [80, 65],
      [130, 20],
      [100, -20],
      [70, -70],
      [55, -105],
      [0, -115],
      [-250, 17],
      [65, -97],
    ];

export const getInitialCoordinates = () => {
  const randomIndex = Math.floor(Math.random() * initCoordinates.length);
  const hexCoordinates = initCoordinates[randomIndex];
  return {
    worldX: -hexCoordinates[0] * hexWidth,
    worldY: -hexCoordinates[1] * hexHeight,
  };
};
